// Settings -> Configuration views (documentation/configuration.md). Pure presentation: data and
// callbacks come from ConfigurationArea. Secrets are shown only as status metadata; there is no
// way to display a secret value because the server never sends one.
import type { ReactNode } from 'react';
import { TONES, statusPresentation, SUBSECTIONS, type ConfigurationSubsection } from './configurationSections';
import type {
  AuditEntry, ConfigSection, CredentialView, DNSObservation, DeploymentSettings, DeploymentView, EntraView, HetznerSettings, HetznerView, Issue,
  LastCheck, LiveKitView, OffsiteBackup, ReportMeta, SecretStatus, SimplySettings, SimplyView, TLSView,
} from '../../../services/configuration';

export function StatusBadge({ state }: { state: string | undefined }) {
  const { label, tone } = statusPresentation(state);
  return (
    <span data-status={state || 'unknown'} className={`inline-block text-xs font-semibold px-2 py-0.5 rounded border whitespace-nowrap ${TONES[tone]}`}>
      {label}
    </span>
  );
}

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

export function Card({ title, status, children, actions }: { title: string; status?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="bg-gray-800 border border-gray-700 rounded-lg p-5 mb-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <div className="flex items-center gap-2">
          {actions}
          {status !== undefined && <StatusBadge state={status} />}
        </div>
      </div>
      {children}
    </section>
  );
}

export function Rows({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-1 md:grid-cols-[14rem_1fr] gap-x-4 gap-y-2 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-gray-400">{k}</dt>
          <dd className="text-white break-words">{v === '' || v === undefined || v === null ? <span className="text-gray-500">not set</span> : v}</dd>
        </div>
      ))}
    </dl>
  );
}

const mono = (v?: string) => (v ? <span className="font-mono text-xs">{v}</span> : '');

/** A secret is described, never shown. */
export function SecretRow({ status }: { status: SecretStatus }) {
  if (!status.configured) {
    return <span><StatusBadge state={status.error ? 'invalid' : 'not_configured'} />{status.error && <span className="ml-2 text-gray-400">({status.error})</span>}</span>;
  }
  return (
    <span>
      <StatusBadge state="configured" />
      <span className="ml-2 text-gray-400">source: {status.source}{status.updated_at ? `, updated ${when(status.updated_at)}` : ''} · value never displayed</span>
    </span>
  );
}

function CredentialRow({ credential }: { credential: CredentialView }) {
  return (
    <span>
      <StatusBadge state={credential.state} />
      <span className="ml-2 text-gray-400">
        held by the {credential.held_by}, not by RadioOS
        {credential.source ? ` · ${credential.source}` : ''}
        {credential.last_verified_at ? ` · verified ${when(credential.last_verified_at)}` : ''}
      </span>
    </span>
  );
}

function ReportRow({ report }: { report: ReportMeta }) {
  const text: Record<ReportMeta['state'], string> = {
    not_configured: 'no control-plane status directory is configured for this system',
    not_reported: 'the control plane has not reported yet',
    invalid: `report rejected (${report.error || 'invalid'}); none of its content is used`,
    reported: `reported ${when(report.generated_at)}`,
  };
  return <span className="text-gray-300">{text[report.state]}</span>;
}

export function LastCheckRow({ check }: { check: LastCheck | null }) {
  if (!check) return <span className="text-gray-500">never tested</span>;
  return (
    <span>
      <StatusBadge state={check.success ? 'connected' : 'verification_failed'} />
      <span className="ml-2 text-gray-400">{when(check.at)} by {check.actor || 'unknown'} · {check.detail}</span>
    </span>
  );
}

function Issues({ issues }: { issues: Issue[] }) {
  if (issues.length === 0) return null;
  return (
    <ul className="mt-4 space-y-1 text-sm">
      {issues.map((i) => (
        <li key={i.field + i.code} className={i.severity === 'error' ? 'text-red-300' : 'text-yellow-200'}>
          {i.severity === 'error' ? '✗' : '•'} {i.message}
        </li>
      ))}
    </ul>
  );
}

export const Button = ({ children, onClick, disabled, kind = 'secondary' }: { children: ReactNode; onClick: () => void; disabled?: boolean; kind?: 'primary' | 'secondary' }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50 ${kind === 'primary' ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-100'}`}
  >
    {children}
  </button>
);

function TextInput({ label, value, onChange, placeholder, help }: { label: string; value: string | number; onChange: (v: string) => void; placeholder?: string; help?: string }) {
  return (
    <label className="block text-sm">
      <span className="text-gray-300">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        className="mt-1 w-full bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-white font-mono text-xs"
      />
      {help && <span className="block mt-1 text-xs text-gray-500">{help}</span>}
    </label>
  );
}

// --- Navigation -----------------------------------------------------------------------------

export function ConfigurationNav({ active, statuses, onSelect }: { active: ConfigurationSubsection; statuses: Partial<Record<ConfigSection, string>>; onSelect: (s: ConfigurationSubsection) => void }) {
  return (
    <nav className="flex flex-wrap gap-2 mb-6" aria-label="Configuration sections">
      {SUBSECTIONS.map((s) => (
        <button
          key={s.id}
          onClick={() => onSelect(s.id)}
          title={s.hint}
          className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${active === s.id ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'}`}
        >
          {s.label}
          {s.id in statuses && <StatusBadge state={statuses[s.id as ConfigSection]} />}
        </button>
      ))}
    </nav>
  );
}

// --- Sections -------------------------------------------------------------------------------

export function EntraPanel({ view, onTest, busy }: { view: EntraView; onTest: () => void; busy: boolean }) {
  const expiry = view.credential_expires
    ? `${when(view.credential_expires)}${view.credential_expires_in_days !== undefined ? ` (in ${view.credential_expires_in_days} days)` : ''}`
    : '';
  return (
    <>
      <Card title="Microsoft Entra ID" status={view.status} actions={<Button onClick={onTest} disabled={busy || !view.enabled}>{busy ? 'Testing…' : 'Test connection'}</Button>}>
        <Rows rows={[
          ['Sign-in', view.enabled ? 'Enabled' : 'Disabled'],
          ['Tenant ID', mono(view.tenant_id)],
          ['Client ID', mono(view.client_id)],
          ['Redirect URI', mono(view.redirect_url)],
          ['Post-logout URI', mono(view.post_logout_url)],
          ['Client secret', <SecretRow status={view.client_secret} />],
          ['Secret key ID', mono(view.credential_key_id)],
          ['Secret expires', expiry],
          ['Password login', view.password_login_enabled ? 'Enabled' : 'Disabled'],
          ['Sessions', `Management Client ${view.session.admin_session_minutes ?? '?'} min, Dispatcher ${view.session.dispatcher_session_hours ?? '?'} h from sign-in (Configuration -> Sessions), ${view.session.cookie_secure ? 'Secure cookie' : 'insecure cookie (local only)'}`],
          ['Linked identities', `${view.linked_identities} (${view.linked_platform_admins} platform admin)`],
          ['Last verification', <LastCheckRow check={view.last_check} />],
        ]} />
        <p className="mt-4 text-xs text-gray-500">Managed by {view.managed_by}. The client secret stays in the external secret store; RadioOS shows only its status.</p>
      </Card>
    </>
  );
}

export function LiveKitPanel({ view, onTest, busy }: { view: LiveKitView; onTest: () => void; busy: boolean }) {
  return (
    <Card title="LiveKit media server" status={view.status} actions={<Button onClick={onTest} disabled={busy}>{busy ? 'Testing…' : 'Test connection'}</Button>}>
      <Rows rows={[
        ['Public URL (clients)', mono(view.public_url)],
        ['Media host', mono(view.media_host)],
        ['Internal URL (backend)', mono(view.internal_url)],
        ['API key', mono(view.api_key)],
        ['API secret', <SecretRow status={view.api_secret} />],
        ['Public profile', view.public_profile.active ? 'Active' : 'Not active (local development)'],
        ['Public URL check', <StatusBadge state={view.public_profile.public_url_valid ? 'ok' : view.public_profile.active ? 'invalid' : 'not_applicable'} />],
        ['Last verification', <LastCheckRow check={view.last_check} />],
      ]} />
      <p className="mt-4 text-xs text-gray-500">Managed by {view.managed_by}.</p>
    </Card>
  );
}

const HETZNER_FIELDS: { key: keyof HetznerSettings; label: string; placeholder?: string }[] = [
  { key: 'project', label: 'Project', placeholder: 'Hetzner Cloud project name' },
  { key: 'location', label: 'Location', placeholder: 'nbg1 | fsn1 | hel1 | ash | hil | sin' },
  { key: 'server_type', label: 'Server type', placeholder: 'e.g. cx43' },
  { key: 'image', label: 'Image', placeholder: 'e.g. ubuntu-24.04' },
  { key: 'server_name', label: 'Server name' },
  { key: 'ssh_key_name', label: 'Deployment SSH key name' },
  { key: 'network_range', label: 'Private network', placeholder: 'e.g. 10.200.0.0/16' },
  { key: 'subnet_range', label: 'Subnet', placeholder: 'e.g. 10.200.10.0/24' },
  { key: 'private_ipv4', label: 'Server private IPv4', placeholder: 'e.g. 10.200.10.10' },
];

function OffsiteRow({ backup }: { backup?: OffsiteBackup }) {
  if (!backup?.last_at) return <StatusBadge state="not_reported" />;
  return (
    <span>
      <StatusBadge state={backup.result === 'failed' ? 'failed' : 'ok'} />
      <span className="ml-2 text-gray-400">{when(backup.last_at)}{backup.snapshot ? ` · snapshot ${backup.snapshot.slice(0, 8)}` : ''} · reported by the deployment</span>
    </span>
  );
}

export function HetznerPanel({ view, form, onChange, onSave, onValidate, busy }: {
  view: HetznerView; form: HetznerSettings; onChange: (f: HetznerSettings) => void; onSave: () => void; onValidate: () => void; busy: boolean;
}) {
  const set = (k: keyof HetznerSettings) => (v: string) => onChange({ ...form, [k]: v });
  const srv = view.server;
  const net = view.network;
  const bucket = view.object_storage;
  return (
    <>
      <Card title={`Hetzner Cloud — ${view.environment}`} status={view.status}>
        <h4 className="text-sm font-semibold text-gray-200 mb-2">Actual state (reported by the control plane)</h4>
        <Rows rows={[
          ['Provider', 'Hetzner Cloud'],
          ['Server', <span><StatusBadge state={srv ? srv.status : view.instance.state} /> <span className="ml-2 text-gray-400">{view.server_basis}</span></span>],
          ['Server name / ID', srv ? <span>{srv.name} <span className="font-mono text-xs text-gray-400">· {srv.id}</span></span> : ''],
          ['Location / server type', srv ? `${srv.location} / ${srv.server_type}` : ''],
          ['Image', mono(srv?.image)],
          ['Public IPv4', mono(srv?.public_ipv4)],
          ['Private IPv4', mono(srv?.private_ipv4)],
          ['Private network / subnet', net ? <span className="font-mono text-xs">{net.name ? `${net.name} ` : ''}{net.ip_range}{net.subnets?.length ? ` · ${net.subnets.join(', ')}` : ''}</span> : ''],
          ['Firewall', srv?.firewall ? <StatusBadge state={srv.firewall === 'none' ? 'missing' : srv.firewall === 'pending' ? 'pending' : 'applied'} /> : ''],
          ['DNS endpoint', view.dns_endpoint ? mono(view.dns_endpoint) : <span className="text-gray-500">not selected / not reported</span>],
          ['API credential', <CredentialRow credential={view.credential} />],
          ['Connection', view.connection ? <span><StatusBadge state={view.connection.state} /> <span className="ml-2 text-gray-400">{when(view.connection.checked_at)}{view.connection.error_code ? ` · ${view.connection.error_code}` : ''}</span></span> : <StatusBadge state="not_reported" />],
          ['Last operation', view.last_operation ? `${view.last_operation.operation}: ${view.last_operation.result} (${when(view.last_operation.at)})${view.last_operation.summary ? ` — ${view.last_operation.summary}` : ''}` : ''],
          ['Control-plane report', <ReportRow report={view.report} />],
        ]} />
        {view.status === 'stale_report' && (
          <p className="mt-3 text-sm text-yellow-200">The report is older than 7 days. Refresh it with <span className="font-mono">radioos-infra report hetzner</span> (control plane).</p>
        )}
        {view.drift.length > 0 && (
          <div className="mt-4"><h4 className="text-sm font-semibold text-red-300">Drift (desired ≠ actual)</h4>
            <ul className="text-sm text-red-200 list-disc ml-5">{view.drift.map((d) => <li key={d}>{d}</li>)}</ul></div>
        )}
      </Card>
      <Card title="Backup storage — Hetzner Object Storage" status={bucket ? (bucket.object_lock === 'enabled' && bucket.versioning === 'enabled' && bucket.retention_mode ? 'enabled' : 'disabled') : 'not_reported'}>
        <Rows rows={[
          ['Bucket', bucket ? <span className="font-mono text-xs">{bucket.bucket} · {bucket.location}</span> : <span className="text-gray-500">not reported</span>],
          ['Object Lock', bucket ? <StatusBadge state={bucket.object_lock} /> : ''],
          ['Versioning', bucket ? <StatusBadge state={bucket.versioning} /> : ''],
          ['Default retention', bucket ? (bucket.retention_mode ? `${bucket.retention_mode} / ${bucket.retention_days} days` : <StatusBadge state="none" />) : ''],
          ['Last off-site backup', <OffsiteRow backup={view.offsite_backup} />],
        ]} />
        <p className="mt-4 text-xs text-gray-500">
          Protection is read back from the S3 API by the control plane. Backups and restores are run by radioosctl on the server
          (documentation/public-test-deployment.md); the S3 keys and the repository password are never held by RadioOS.
        </p>
      </Card>
      <Card title="Desired configuration" status={view.complete ? 'configured' : 'not_configured'}
        actions={<><Button onClick={onValidate} disabled={busy}>Validate</Button><Button kind="primary" onClick={onSave} disabled={busy}>Save</Button></>}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {HETZNER_FIELDS.map((f) => <TextInput key={f.key} label={f.label} value={form[f.key]} onChange={set(f.key)} placeholder={f.placeholder} />)}
        </div>
        <Issues issues={view.issues} />
        {view.comparison.length > 0 && (
          <table className="w-full text-sm mt-4" data-comparison>
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700"><th className="py-2 pr-3">Field</th><th className="pr-3">Desired (stored)</th><th className="pr-3">Actual (reported)</th><th>State</th></tr>
            </thead>
            <tbody>
              {view.comparison.map((c) => (
                <tr key={c.field} className="border-b border-gray-700/60">
                  <td className="py-1.5 pr-3 text-gray-300">{HETZNER_FIELDS.find((f) => f.key === c.field)?.label || c.field}</td>
                  <td className="pr-3 font-mono text-xs">{c.desired}</td>
                  <td className="pr-3 font-mono text-xs">{c.actual || <span className="text-gray-500">{c.field === 'project' ? 'not exposed by the Hetzner API' : 'not reported'}</span>}</td>
                  <td><StatusBadge state={c.state} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mt-4 text-xs text-gray-500">
          Non-secret choices only, stored in RadioOS and audited{view.meta.updated_at ? ` (last change ${when(view.meta.updated_at)} by ${view.meta.updated_by || 'unknown'})` : ''}.
          They are applied by OpenTofu (infra/) only through an operator-approved plan; the Hetzner API token is held by the provisioning control plane,
          never by RadioOS. Nothing on this page creates, changes or deletes Hetzner resources.
        </p>
      </Card>
    </>
  );
}

function DNSCell({ o }: { o: DNSObservation }) {
  return (
    <span>
      <StatusBadge state={o.state} />
      {o.addresses.length > 0 && <span className="ml-2 font-mono text-xs text-gray-300">{o.addresses.join(', ')}</span>}
    </span>
  );
}

export function SimplyPanel({ view, form, onChange, onSave, onValidate, busy }: {
  view: SimplyView; form: SimplySettings; onChange: (f: SimplySettings) => void; onSave: () => void; onValidate: () => void; busy: boolean;
}) {
  return (
    <>
      <Card title={`Simply DNS — ${view.desired.zone || 'no zone'}`} status={view.status}>
        <table className="w-full text-sm mb-4">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="py-2 pr-3">Record</th><th className="pr-3">Desired</th><th className="pr-3">At Simply (reported)</th><th className="pr-3">Public DNS (observed)</th><th>State</th>
            </tr>
          </thead>
          <tbody>
            {view.records.map((r) => (
              <tr key={r.name} className="border-b border-gray-700/60 align-top">
                <td className="py-2 pr-3 font-mono text-xs text-white">{r.type} {r.name}</td>
                <td className="pr-3 text-xs">{r.desired_value ? <span className="font-mono">{r.desired_value}</span> : <span className="text-gray-400">{r.desired_basis}</span>}</td>
                <td className="pr-3 text-xs">{r.provider_value ? <span className="font-mono">{r.provider_value}</span> : <span className="text-gray-500">not reported</span>}</td>
                <td className="pr-3"><DNSCell o={r.public} /></td>
                <td><StatusBadge state={r.sync} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <Rows rows={[
          ['DNS API credential', <CredentialRow credential={view.credential} />],
          ['Connection', view.connection ? <StatusBadge state={view.connection.state} /> : <StatusBadge state="not_reported" />],
          ['Last operation', view.last_operation ? `${view.last_operation.operation}: ${view.last_operation.result} (${when(view.last_operation.at)})` : ''],
          ['Control-plane report', <ReportRow report={view.report} />],
        ]} />
        <p className="mt-4 text-xs text-gray-500">
          The record set is derived from Deployment / System (public and media host). Public DNS is looked up by the RadioOS backend; changes at
          Simply are made only by the provisioning control plane with its own API key. Nothing on this page changes DNS.
        </p>
      </Card>
      <Card title="Desired configuration" status={view.complete ? 'configured' : 'not_configured'}
        actions={<><Button onClick={onValidate} disabled={busy}>Validate</Button><Button kind="primary" onClick={onSave} disabled={busy}>Save</Button></>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput label="Zone" value={form.zone} onChange={(v) => onChange({ ...form, zone: v })} />
          <TextInput label="TTL (seconds)" value={form.ttl || ''} onChange={(v) => onChange({ ...form, ttl: Number(v) || 0 })} />
        </div>
        <Issues issues={view.issues} />
      </Card>
    </>
  );
}

/** Certificate health as the deployment reports it; RadioOS never manages the certificate itself. */
export function TLSCard({ tls }: { tls: TLSView }) {
  const r = tls.renewal;
  const expiry = tls.not_after ? `${when(tls.not_after)}${tls.days_remaining !== undefined ? ` (${tls.days_remaining} days left)` : ''}` : '';
  return (
    <Card title="TLS certificate" status={tls.health}>
      <Rows rows={[
        ['Certificate', <span><StatusBadge state={tls.state} /> <span className="ml-2 text-gray-400">{tls.basis}</span></span>],
        ['Issuer', tls.issuer || ''],
        ['Host names', tls.names.length ? <span className="font-mono text-xs">{tls.names.join(', ')}</span> : ''],
        ['Expires', expiry],
        ['Automatic renewal', r ? <span><StatusBadge state={r.scheduler === 'none' ? 'not_configured' : r.enabled ? 'enabled' : 'disabled'} />
          <span className="ml-2 text-gray-400">{r.scheduler === 'systemd-timer' ? 'systemd timer, twice daily' : 'none'}{r.next_check_at ? ` · next check ${when(r.next_check_at)}` : ''}</span></span> : <StatusBadge state="not_reported" />],
        ['Last renewal check', r?.last_check_at ? <span><StatusBadge state={r.last_result} /> <span className="ml-2 text-gray-400">{when(r.last_check_at)}</span></span> : <span className="text-gray-500">none recorded</span>],
        ['Last renewal', r?.last_renewed_at ? when(r.last_renewed_at) : <span className="text-gray-500">none since monitoring started</span>],
        ['Last renewal dry-run', r?.last_dry_run_at ? when(r.last_dry_run_at) : <span className="text-gray-500">none recorded</span>],
      ]} />
      {tls.reasons.length > 0 && (
        <ul className={`mt-4 text-sm list-disc ml-5 ${tls.health === 'critical' ? 'text-red-200' : 'text-yellow-200'}`} data-tls-reasons>
          {tls.reasons.map((x) => <li key={x}>{x}</li>)}
        </ul>
      )}
      <p className="mt-4 text-xs text-gray-500">
        Healthy: issued, both host names covered, renewal timer enabled, a successful renewal check within 3 days and at least 30 days left.
        Warning below 30 days or on a failed/stale check; critical below 14 days, when expired, a host name is missing or renewal is off.
        Renewal runs on the server (radioosctl tls-renew); RadioOS only displays the reported state.
      </p>
    </Card>
  );
}

export function DeploymentPanel({ view, form, onChange, onSave, onValidate, busy }: {
  view: DeploymentView; form: DeploymentSettings; onChange: (f: DeploymentSettings) => void; onSave: () => void; onValidate: () => void; busy: boolean;
}) {
  const { instance: i, target: t } = view;
  return (
    <>
      <Card title="This system">
        <Rows rows={[
          ['Environment', i.environment],
          ['Deployment profile', i.profile === 'public' ? 'Public (Internet-facing, fail-closed)' : 'Local development'],
          ['DEV_MODE', i.dev_mode ? <span className="text-yellow-200">ON (local development identity — never on a public stack)</span> : 'Off'],
          ['Backend release', i.release || <span className="text-gray-500">unknown (local build)</span>],
          ['Public origin', mono(i.public_origin)],
          ['Media URL', mono(i.media_url)],
          ['Microsoft sign-in', i.entra_enabled ? 'Enabled' : 'Disabled'],
          ['Password login', i.password_login_enabled ? 'Enabled' : 'Disabled'],
          ['Control-plane reports', i.control_plane_reports ? 'Directory configured' : 'Not configured'],
        ]} />
      </Card>
      <Card title={`Deployment target — ${t.environment}`} status={t.deployment.state}>
        <Rows rows={[
          ['VM', <span><StatusBadge state={t.vm.state} /> <span className="ml-2 text-gray-400">{t.vm.basis}</span></span>],
          ['Public IPv4', t.public_ipv4 ? mono(t.public_ipv4) : <span className="text-gray-500">not provisioned</span>],
          ...t.dns.map((o): [string, ReactNode] => [o.name, <DNSCell o={o} />]),
          ['TLS certificate', <span><StatusBadge state={t.tls.health === 'unknown' ? t.tls.state : t.tls.health} /> <span className="ml-2 text-gray-400">{t.tls.not_after ? `expires ${when(t.tls.not_after)}${t.tls.days_remaining !== undefined ? ` (${t.tls.days_remaining} days)` : ''} · ` : ''}{t.tls.basis}</span></span>],
          ['Deployment', <span><StatusBadge state={t.deployment.state} /> <span className="ml-2 text-gray-400">{t.deployment.release ? `${t.deployment.release} · ` : ''}{t.deployment.basis}</span></span>],
          ['Last deployment', when(t.deployment.deployed_at)],
          ['Backup', <span><StatusBadge state={t.backup.state} /> <span className="ml-2 text-gray-400">{when(t.backup.last_at)}</span></span>],
          ['Off-site backup', <OffsiteRow backup={t.backup.offsite} />],
          ['Deployment report', <ReportRow report={t.report} />],
        ]} />
        {t.drift.length > 0 && (
          <div className="mt-4"><h4 className="text-sm font-semibold text-red-300">Drift</h4>
            <ul className="text-sm text-red-200 list-disc ml-5">{t.drift.map((d) => <li key={d}>{d}</li>)}</ul></div>
        )}
        <div className="mt-4" data-blockers>
          <h4 className="text-sm font-semibold text-gray-200">Blockers</h4>
          {t.blockers.length === 0
            ? <p className="text-sm text-green-300">None reported.</p>
            : <ul className="text-sm text-yellow-200 list-disc ml-5">{t.blockers.map((b) => <li key={b}>{b}</li>)}</ul>}
        </div>
      </Card>
      <TLSCard tls={t.tls} />
      <Card title="Desired public names" actions={<><Button onClick={onValidate} disabled={busy}>Validate</Button><Button kind="primary" onClick={onSave} disabled={busy}>Save</Button></>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput label="Public host (Management Client, API)" value={form.public_host} onChange={(v) => onChange({ ...form, public_host: v })} />
          <TextInput label="Media host (LiveKit)" value={form.media_host} onChange={(v) => onChange({ ...form, media_host: v })} />
        </div>
        <Issues issues={t.issues} />
        <p className="mt-4 text-xs text-gray-500">
          Source of the managed DNS records and of the deployment's PUBLIC_HOST/MEDIA_HOST. A deployment serving other names is reported as drift.
        </p>
      </Card>
    </>
  );
}

export function AuditTable({ entries }: { entries: AuditEntry[] }) {
  return (
    <Card title="Configuration audit">
      {entries.length === 0 ? <p className="text-sm text-gray-500">No configuration operations recorded.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-400 border-b border-gray-700"><th className="py-2 pr-3">When</th><th className="pr-3">Who</th><th className="pr-3">Provider</th><th className="pr-3">Operation</th><th className="pr-3">Result</th><th>Details</th></tr></thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-gray-700/60 align-top">
                <td className="py-2 pr-3 text-gray-300 whitespace-nowrap">{when(e.created_at)}</td>
                <td className="pr-3">{e.actor || 'unknown'}</td>
                <td className="pr-3">{e.provider}</td>
                <td className="pr-3">{e.operation.replace(/_/g, ' ')}</td>
                <td className="pr-3"><StatusBadge state={e.success ? 'ok' : 'failed'} /></td>
                <td className="text-xs text-gray-300">
                  {e.detail}
                  {e.changed_fields.length > 0 && <div className="font-mono text-gray-400">{e.changed_fields.map((c) => `${c.field}${c.to !== null && c.to !== undefined ? `=${String(c.to)}` : ''}`).join(', ')}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
